/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { isSubscribeRoute, lockForStatus } from './inactive/subscription-lock';
import type { AgencyStatus } from '@/features/onboarding/types/agency-dashboard';

interface SubscriptionGateAgency {
  id?: string;
  name?: string;
  type?: 'agency' | 'solo_broker';
  effective_status?: AgencyStatus | null;
}

interface SubscriptionGateProps {
  agency: SubscriptionGateAgency;
  children: ReactNode;
}

/**
 * Gates the agency shell on the subscription.
 *
 * This is the outer half of the gate and it answers one question: does the
 * agency open at all? Without a subscription it does not, and there is only
 * one thing to do about that, so the route goes straight to the plans rather
 * than through a screen whose whole content was a button leading there.
 *
 * Suspended and cancelled agencies do open, with most areas closed; that part
 * lives in `LockedAreaGate`, inside the shell.
 *
 * @param props - The agency and the protected children.
 * @returns The children, or a redirect to the plans.
 */
export function SubscriptionGate({ agency, children }: SubscriptionGateProps) {
  const { pathname } = useLocation();
  /*
   * The id comes from the route, not from the store: the store can still be
   * holding the previous agency — or nothing — on the first render, and a
   * redirect built from an empty id lands on a path no route matches, which
   * the catch-all answers by sending the buyer to the workspace.
   */
  const { agency_id: routeId } = useParams<{ agency_id: string }>();
  const agencyId = routeId ?? agency.id;

  if (lockForStatus(agency.effective_status) !== 'draft' || isSubscribeRoute(pathname) || !agencyId) {
    return <>{children}</>;
  }

  return <Navigate to={`/app/agencies/${agencyId}/subscribe?step=plan`} replace />;
}
